import { ProgressHTMLAttributes, ReactElement } from "react";
import { t } from "@lingui/core/macro";

import * as S from "./styled";

export type ProgressBarKind = "info" | "warning" | "danger";

type ProgressBarProps = Omit<
  ProgressHTMLAttributes<HTMLProgressElement>,
  "value" | "max"
> & {
  /**
   * The title / label of the progress bar.
   */
  title?: string;

  /**
   * The current value of the progress bar.
   */
  value?: number;

  /**
   * The maximum value of the progress bar.
   */
  max?: number;

  /**
   * Changes the color of the progress bar. Typically you'd use "info" for
   * normal progress, but "warning" or "danger" if you want to alert the user of
   * a failure during progress.
   * */
  kind?: ProgressBarKind;
};
export const ProgressBar = ({
  title,
  value = 0,
  max = 1,
  kind = "info",
}: ProgressBarProps): ReactElement => {
  const progressPercent =
    max > 0 ? Math.min(Math.max((value / max) * 100, 0), 100) : 0;
  // If the value is greater than 0, calculate the percentage.
  // Otherwise, show "Pending".
  const percentage =
    value > 0 ? `${Math.round(progressPercent)}%` : t`common.pending`;

  return (
    <S.Wrapper>
      <S.Label>
        <S.Header>
          <S.Title>{title}</S.Title>
          <S.Percentage>{percentage}</S.Percentage>
        </S.Header>
        <S.Progress value={value} max={max} />
        <S.Track aria-hidden="true">
          <S.Fill $progressPercent={progressPercent} $kind={kind} />
        </S.Track>
      </S.Label>
    </S.Wrapper>
  );
};
